// client/src/components/event/ThemedDayConfig.jsx
/**
 * =================================================================
 * Themed Feed Day Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set hashtag, theme description, post guidelines
 * 2. Schedule date and optional end date
 * 3. Display shareable hyperlink
 * 4. View posts with feature toggle
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import DateTimeSelect from "../DateTimeSelect";
import "./AuctionConfig.css";

const ThemedDayConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Themed Day",
    hashtag: "",
    theme_description: "",
    post_guidelines: "",
    recurring: "ONCE",
    scheduled_at: "",
    end_at: "",
    status: "NEW",
  });

  const [posts, setPosts] = useState([]);
  const [showPosts, setShowPosts] = useState(false);
  const [postCount, setPostCount] = useState(0);
  const [configExists, setConfigExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=themed_day`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.themed_day) {
        const c = response.data.themed_day;
        setConfigExists(true);
        setConfig({
          name: c.name || itemName || "Themed Day",
          hashtag: c.hashtag || "",
          theme_description: c.theme_description || "",
          post_guidelines: c.post_guidelines || "",
          recurring: c.recurring || "ONCE",
          scheduled_at: formatDateForInput(c.scheduled_at),
          end_at: formatDateForInput(c.end_at),
          status: c.status || "NEW",
        });
        loadPostCount();
      } else {
        setConfigExists(false);
        setMessage({
          text: "No Themed Day configured yet. Fill out the form below to create one.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading Themed Day config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "No Themed Day configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load Themed Day configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPostCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/posts`
      );
      if (response.data.success) {
        setPostCount(response.data.count || response.data.posts?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadPosts = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/posts`
      );
      if (response.data.success) {
        setPosts(response.data.posts);
        setPostCount(response.data.count || response.data.posts.length);
        setShowPosts(true);
      }
    } catch (err) {
      console.error("Error loading posts:", err);
      setMessage({ text: "Failed to load posts", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Theme name is required");
    }
    if (config.scheduled_at && config.end_at) {
      if (new Date(config.end_at) <= new Date(config.scheduled_at)) {
        errors.push("End date must be after start date");
      }
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        hashtag: config.hashtag || null,
        theme_description: config.theme_description || null,
        post_guidelines: config.post_guidelines || null,
        recurring: config.recurring,
        scheduled_at: formatDateTimeForBackend(config.scheduled_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "Themed Day configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving Themed Day config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save Themed Day configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this Themed Day? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "Themed Day cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling Themed Day:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel Themed Day",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading Themed Day configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>Themed Day Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link for participants to submit their themed posts:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {shareLink}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {configExists && (
        <div className="auction-stats-section">
          <h3>Theme Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Posts</div>
              <div className="stat-value">{postCount}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadPosts}>
              View Posts
            </button>
          </div>
        </div>
      )}

      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            Theme Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter theme name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="hashtag">Hashtag</label>
          <input
            type="text"
            id="hashtag"
            value={config.hashtag}
            onChange={(e) => handleInputChange("hashtag", e.target.value)}
            placeholder="#ThemeHashtag"
            disabled={isComplete}
          />
        </div>

        <div className="form-group">
          <label htmlFor="theme_description">Theme Description</label>
          <textarea
            id="theme_description"
            value={config.theme_description}
            onChange={(e) => handleInputChange("theme_description", e.target.value)}
            placeholder="Describe the theme for the day"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="post_guidelines">Post Guidelines</label>
          <textarea
            id="post_guidelines"
            value={config.post_guidelines}
            onChange={(e) => handleInputChange("post_guidelines", e.target.value)}
            placeholder="Guidelines for posts (format, content requirements, etc.)"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="scheduled_at">Start Date & Time</label>
            <DateTimeSelect
              id="scheduled_at"
              value={config.scheduled_at}
              onChange={(v) => handleInputChange("scheduled_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <DateTimeSelect
              id="end_at"
              value={config.end_at}
              onChange={(v) => handleInputChange("end_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This themed day is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel Themed Day
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {showPosts && (
        <div className="modal-overlay" onClick={() => setShowPosts(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Posts ({posts.length})</h3>
              <button className="close-button" onClick={() => setShowPosts(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Post</th>
                    <th>Platform</th>
                    <th>Featured</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post, index) => (
                    <tr key={post.id}>
                      <td>{index + 1}</td>
                      <td>{post.name}</td>
                      <td>
                        {post.post_url ? (
                          <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          post.post_text || "—"
                        )}
                      </td>
                      <td>{post.platform || "—"}</td>
                      <td>{post.featured ? "Yes" : "No"}</td>
                      <td>{new Date(post.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemedDayConfig;
